import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Send, History, Clock, Users, Zap } from 'lucide-react';
import { AdminShell } from '@/components/admin-shell';
import { FgButton } from '@/components/fg-button';
import { FgInput } from '@/components/fg-input';
import { FgToast } from '@/components/fg-toast';
import { Bento, PageHero, Pill, Widget } from '@/components/Widget';
import { CustomSelect } from '@/components/ui/CustomSelect';
import { CustomTextarea } from '@/components/ui/CustomTextarea';
import {
  adminNotificationsApi,
  type NotificationType,
  type ChannelType,
  type BroadcastTarget,
  type AdminNotification,
} from '@/lib/notifications-api';

const NOTIFICATION_TYPES: { value: NotificationType; label: string }[] = [
  { value: 'admin_broadcast', label: 'Admin Broadcast' },
  { value: 'lesson_unlocked', label: 'Lesson Unlocked' },
  { value: 'level_completed', label: 'Level Completed' },
  { value: 'subscription_expiring', label: 'Subscription Expiring' },
  { value: 'ai_credits_low', label: 'AI Credits Low' },
  { value: 'speaking_session_ready', label: 'Speaking Session Ready' },
  { value: 'weekly_progress', label: 'Weekly Progress' },
];

const CHANNEL_OPTIONS: { value: ChannelType; label: string }[] = [
  { value: 'all', label: 'All Channels (Push + Email + In-App)' },
  { value: 'push', label: 'Push Notification' },
  { value: 'email', label: 'Email' },
  { value: 'in_app', label: 'In-App Notification' },
];

const BROADCAST_TARGETS: { value: BroadcastTarget; label: string }[] = [
  { value: 'all', label: 'All Users' },
  { value: 'active', label: 'Active Users' },
  { value: 'expiring_soon', label: 'Users with Expiring Subscription' },
  { value: 'low_credits', label: 'Users with Low Credits' },
];

type Tab = 'send' | 'history';

export function NotificationsPage() {
  const [tab, setTab] = useState<Tab>('send');
  return (
    <AdminShell>
      <Bento>
        <PageHero
          accent="blue"
          eyebrow="Communications"
          title="Notifications"
          description="Send targeted notifications to users via push, email, or in-app."
          aside={
            <div className="flex gap-2">
              <span className={tab === 'send' ? 'fg-pill-blue' : 'fg-pill-neutral'}>
                <Pill
                  accent={tab === 'send' ? 'blue' : 'neutral'}
                >
                  <Send size={12} />
                  Send
                </Pill>
              </span>
              <button
                onClick={() => setTab('history')}
                className={`fg-cta-secondary ${tab === 'history' ? 'ring-1 ring-white/20' : ''}`}
              >
                <History size={12} />
                History
              </button>
            </div>
          }
        />
        {tab === 'send' ? <SendNotificationPanel /> : <NotificationHistoryPanel />}
      </Bento>
    </AdminShell>
  );
}

function SendNotificationPanel() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'single' | 'broadcast'>('single');
  const [userId, setUserId] = useState('');
  const [target, setTarget] = useState<BroadcastTarget>('all');
  const [type, setType] = useState<NotificationType>('admin_broadcast');
  const [channel, setChannel] = useState<ChannelType>('all');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        userId: mode === 'single' ? userId : 'broadcast',
        type,
        title,
        body,
        deepLink: deepLink || undefined,
        channel,
      };
      if (mode === 'broadcast') {
        return adminNotificationsApi.broadcast({ ...payload, target } as Parameters<typeof adminNotificationsApi.broadcast>[0]);
      }
      return adminNotificationsApi.send(payload as Parameters<typeof adminNotificationsApi.send>[0]);
    },
    onSuccess: (res) => {
      const data = res.data as { recipients?: number; ok?: boolean };
      FgToast.success(
        mode === 'broadcast'
          ? `Broadcast sent to ${data.recipients ?? 0} users`
          : 'Notification sent successfully',
      );
      qc.invalidateQueries({ queryKey: ['admin', 'notifications'] });
      setTitle('');
      setBody('');
      setDeepLink('');
      setScheduledAt('');
    },
    onError: (e) => {
      FgToast.error((e as { message?: string }).message ?? 'Failed to send notification');
    },
    onSettled: () => setIsSubmitting(false),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      FgToast.error('Title and body are required');
      return;
    }
    if (mode === 'single' && !userId.trim()) {
      FgToast.error('User ID is required for single-user notifications');
      return;
    }
    setIsSubmitting(true);
    sendMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Widget accent="blue" title="Notification Type">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setMode('single')}
            className={`flex-1 p-4 rounded-xl border transition-all ${
              mode === 'single'
                ? 'border-[#FFD60A] bg-[#FFD60A]/10'
                : 'border-white/10 bg-white/5 hover:bg-white/10'
            }`}
          >
            <Users size={20} className={mode === 'single' ? 'text-[#FFD60A]' : 'text-white/50'} />
            <p className="mt-2 text-sm font-bold">Single User</p>
            <p className="text-xs text-white/50">Target one user by ID</p>
          </button>
          <button
            type="button"
            onClick={() => setMode('broadcast')}
            className={`flex-1 p-4 rounded-xl border transition-all ${
              mode === 'broadcast'
                ? 'border-[#FFD60A] bg-[#FFD60A]/10'
                : 'border-white/10 bg-white/5 hover:bg-white/10'
            }`}
          >
            <Zap size={20} className={mode === 'broadcast' ? 'text-[#FFD60A]' : 'text-white/50'} />
            <p className="mt-2 text-sm font-bold">Broadcast</p>
            <p className="text-xs text-white/50">Send to a user segment</p>
          </button>
        </div>
      </Widget>

      <Widget accent="blue" title="Recipients">
        {mode === 'single' ? (
          <div className="space-y-4">
            <FgInput
              label="User ID"
              placeholder="Enter user UUID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <CustomSelect
              label="Target Segment"
              value={target}
              onChange={(v) => setTarget(v as BroadcastTarget)}
              options={BROADCAST_TARGETS}
            />
          </div>
        )}
      </Widget>

      <Widget accent="blue" title="Message Content">
        <div className="space-y-4">
          <CustomSelect
            label="Notification Type"
            value={type}
            onChange={(v) => setType(v as NotificationType)}
            options={NOTIFICATION_TYPES}
          />
          <FgInput
            label="Title"
            placeholder="Notification title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <CustomTextarea
            label="Body"
            placeholder="Notification body text..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
          />
          <FgInput
            label="Deep Link (optional)"
            placeholder="/learn/level/A2/lesson/abc123"
            value={deepLink}
            onChange={(e) => setDeepLink(e.target.value)}
          />
        </div>
      </Widget>

      <Widget accent="blue" title="Delivery">
        <div className="space-y-4">
          <CustomSelect
            label="Channel"
            value={channel}
            onChange={(v) => setChannel(v as ChannelType)}
            options={CHANNEL_OPTIONS}
          />
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-white/50" />
            <FgInput
              label="Schedule (optional)"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          </div>
        </div>
      </Widget>

      <div className="flex justify-end">
        <FgButton
          type="submit"
          disabled={isSubmitting || !title.trim() || !body.trim()}
          className="fg-btn-primary"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Sending...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Send size={14} />
              {mode === 'broadcast' ? 'Send Broadcast' : 'Send Notification'}
            </span>
          )}
        </FgButton>
      </div>
    </form>
  );
}

function NotificationHistoryPanel() {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState('');
  const limit = 20;

  const queryParams = {
    page,
    limit,
    ...(typeFilter ? { type: typeFilter as NotificationType } : {}),
    ...(userFilter ? { userId: userFilter } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'notifications', queryParams],
    queryFn: async () => {
      const res = await adminNotificationsApi.list(queryParams);
      return res.data;
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <Widget accent="blue" title="Filters">
        <div className="flex flex-wrap gap-4">
          <div className="w-48">
            <CustomSelect
              label="Type"
              value={typeFilter}
              onChange={(v) => { setTypeFilter(v); setPage(1); }}
              options={[{ value: '', label: 'All Types' }, ...NOTIFICATION_TYPES]}
            />
          </div>
          <div className="w-64">
            <FgInput
              label="User ID"
              placeholder="Filter by user ID"
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
            />
          </div>
        </div>
      </Widget>

      <Widget accent="blue" title="History" fullWidth>
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 rounded-lg fg-skeleton" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-white/55">No notifications found.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-xs font-mono text-white/50 uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-mono text-white/50 uppercase">Title</th>
                    <th className="text-left py-3 px-4 text-xs font-mono text-white/50 uppercase">User</th>
                    <th className="text-left py-3 px-4 text-xs font-mono text-white/50 uppercase">Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {items.map((n: AdminNotification) => (
                    <tr key={n.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5">
                          <Bell size={12} className="text-[#FFD60A]" />
                          <span className="font-mono text-xs text-white/70">{n.type}</span>
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="max-w-xs">
                          <p className="font-bold text-white truncate">{n.title}</p>
                          <p className="text-xs text-white/50 truncate">{n.body}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-white/50">{n.userId.slice(0, 8)}...</td>
                      <td className="py-3 px-4 text-xs text-white/50">
                        {new Date(n.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-white/50">
                  Page {page} of {totalPages} — {total} total
                </p>
                <div className="flex gap-2">
                  <FgButton
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="fg-btn-secondary"
                  >
                    Previous
                  </FgButton>
                  <FgButton
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="fg-btn-secondary"
                  >
                    Next
                  </FgButton>
                </div>
              </div>
            )}
          </>
        )}
      </Widget>
    </div>
  );
}
